"use client";

import { useRef, useState, useEffect, useCallback } from "react";
import { X, ImageIcon } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { ErrorMessage } from "@clarityos/ui";
import { Label } from "./Label";
import { formatBytes } from "@/lib/formatting";
import { Button } from "@clarityos/ui/button";

export interface FileAttachmentProps {
  value: File | null;
  onChange: (file: File | null) => void;
  errorMessage?: string;
  label?: string;
  optional?: boolean;
  acceptedTypes: string[];
  disabled?: boolean;
  "data-testid"?: string;
}

export function FileAttachment({
  value,
  onChange,
  errorMessage,
  label,
  optional,
  acceptedTypes,
  disabled,
  "data-testid": dataTestId,
}: FileAttachmentProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!value) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(value);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  const processFile = useCallback(
    (file: File) => {
      if (!disabled) onChange(file);
    },
    [disabled, onChange],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = "";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = Array.from(e.clipboardData?.items ?? []);
      const imageItem = items.find((item) => item.type.startsWith("image/"));
      if (!imageItem) return;
      const file = imageItem.getAsFile();
      if (file) processFile(file);
    };
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [processFile]);

  const handleRemove = () => {
    if (!disabled) onChange(null);
  };

  const acceptAttr = acceptedTypes.join(",");
  const inputId = dataTestId
    ? `${dataTestId}-file-attachment`
    : "file-attachment";

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <Label htmlFor={inputId} optional={optional}>
          {label}
        </Label>
      )}

      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={acceptAttr}
        className="sr-only"
        onChange={handleInputChange}
        tabIndex={-1}
      />

      {value && previewUrl ? (
        <div className="flex items-center gap-3 rounded-[10px] border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3">
          <img
            src={previewUrl}
            alt={value.name}
            className="size-10 shrink-0 rounded-[6px] object-cover"
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-medium text-[#1A1F2E]">
              {value.name}
            </p>
            <p className="text-[12px] text-[#6A7282]">
              {formatBytes(value.size)}
            </p>
          </div>
          <Button
            variant="ghost"
            startIcon={({ className }) => (
              <X className={className} strokeWidth={2.5} />
            )}
            onClick={handleRemove}
            disabled={disabled}
            aria-label="Remove attachment"
            data-testid={dataTestId ? `${dataTestId}-remove` : undefined}
          />
        </div>
      ) : (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={twMerge("flex flex-wrap items-center gap-2")}
        >
          <button
            type="button"
            onClick={() => !disabled && inputRef.current?.click()}
            disabled={disabled}
            data-testid={dataTestId ? `${dataTestId}-upload` : undefined}
            className={twMerge(
              "flex flex-1 cursor-pointer items-center gap-3 rounded-[10px] border border-dashed border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 text-left text-[14px] font-medium text-[#1A1F2E] transition-colors",
              "hover:border-[#9CA3AF] hover:bg-[#F3F4F6]",
              isDragging && "border-[#6B7280] bg-[#F3F4F6]",
              disabled && "cursor-not-allowed opacity-50",
            )}
          >
            <ImageIcon className="size-5 shrink-0 text-[#9CA3AF]" />
            <span className="flex flex-col">
              <span>Attach screenshot</span>
              <span className="text-[12px] font-normal text-[#9CA3AF]">
                drag & drop or paste
              </span>
            </span>
          </button>
        </div>
      )}

      {errorMessage && <ErrorMessage message={errorMessage} />}
    </div>
  );
}
